export const HANDS = [
  { id: "left", label: "Left hand" },
  { id: "right", label: "Right hand" },
];

export const FINGERS = [
  { id: "thumb", label: "Thumb", order: 1 },
  { id: "index", label: "Index finger", order: 2 },
  { id: "middle", label: "Middle finger", order: 3 },
  { id: "ring", label: "Ring finger", order: 4 },
  { id: "pinky", label: "Pinky", order: 5 },
];

export function nailKey(handId, fingerId) {
  return `${handId}_${fingerId}`;
}

export function parseDurationMinutes(duration) {
  const text = String(duration || "");
  const ranges = text.match(/\d+/g)?.map(Number) || [];
  if (!ranges.length) return 75;
  if (ranges.length >= 2) return Math.round((ranges[0] + ranges[1]) / 2);
  return ranges[0];
}

export function buildNailPlan(design = {}) {
  const existing = Array.isArray(design.nailPlan) ? design.nailPlan : null;
  if (existing?.length) return existing;

  const totalMinutes = parseDurationMinutes(design.duration);
  const baseMinutes = Math.max(4, Math.round(totalMinutes / 10));
  const colors = Array.isArray(design.colors) ? design.colors.filter(Boolean) : [];
  const products = Array.isArray(design.products) ? design.products.filter(Boolean) : [];
  const technique = design.technique || design.finish || "Detailed gel application";
  const lengthShape = [design.nailLength, design.nailShape].filter(Boolean).join(" ");

  return HANDS.flatMap((hand) =>
    FINGERS.map((finger, index) => {
      const accent = finger.id === "ring" || finger.id === "thumb";
      return {
        key: nailKey(hand.id, finger.id),
        hand: hand.id,
        handLabel: hand.label,
        finger: finger.id,
        fingerLabel: finger.label,
        title: `${hand.label} · ${finger.label}`,
        image_url: design.image_url || null,
        colors,
        products,
        estimatedMinutes: accent ? baseMinutes + 2 : baseMinutes,
        method: buildMethod({ design, technique, lengthShape, accent, index }),
      };
    })
  );
}

function buildMethod({ design, technique, lengthShape, accent, index }) {
  const colorText = Array.isArray(design.colors) && design.colors.length
    ? `Use ${design.colors.slice(0, 3).join(", ")} as the working palette.`
    : "Match the approved design palette.";
  const productText = Array.isArray(design.products) && design.products.length
    ? `Keep ${design.products.slice(0, 5).join(", ")} ready.`
    : "Keep base coat, detail brush, curing lamp, and top coat ready.";
  const accentText = accent
    ? "Treat this as an accent nail: slow down on line work, charms, chrome, or layered details."
    : "Keep this nail consistent with the set and avoid overbuilding the product.";

  return [
    lengthShape ? `Prep and shape to ${lengthShape}.` : "Prep cuticle, refine sidewalls, and balance the nail surface.",
    colorText,
    `${technique}${index > 2 ? " with clean symmetry against the opposite hand" : ""}.`,
    accentText,
    productText,
    "Seal edges, cure fully, inspect under light, then mark this nail done.",
  ];
}

export function normalizeWorkProgress(progress = {}) {
  return typeof progress === "object" && progress && !Array.isArray(progress) ? progress : {};
}

export function planCompletion(plan, progress = {}) {
  const safe = normalizeWorkProgress(progress);
  const total = plan.length;
  const completed = plan.filter((nail) => safe[nail.key]?.status === "done").length;
  return {
    total,
    completed,
    percent: total ? Math.round((completed / total) * 100) : 0,
    complete: total > 0 && completed === total,
  };
}

export function firstOpenNail(plan, progress = {}) {
  const safe = normalizeWorkProgress(progress);
  return plan.find((nail) => safe[nail.key]?.status !== "done") || plan[0] || null;
}
